import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Mail, Phone, Twitter, Youtube } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function Footer() {
  const [s, setS] = useState(null);
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [visitors, setVisitors] = useState(null);

  useEffect(() => {
    api.get("/settings/public").then((r) => setS(r.data)).catch(() => {});
    if (!localStorage.getItem("sp_visited")) {
      localStorage.setItem("sp_visited", "1");
      api.post("/stats/visit").then((r) => setVisitors(r.data.visitors)).catch(() => {});
    } else {
      api.get("/stats/public").then((r) => setVisitors(r.data.visitors)).catch(() => {});
    }
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/subscribe", { email });
      toast.success(data.message);
      setSubscribed(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <footer data-testid="footer" className="border-t border-brand-line bg-brand-ink">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <div data-testid="newsletter-block" className="card-k mb-14 flex flex-col items-start justify-between gap-6 p-8 lg:flex-row lg:items-center">
          <div>
            <p className="eyebrow">NEVER MISS A START LINE</p>
            <h3 className="mt-2 font-display text-2xl font-bold uppercase text-white">Get New Event Alerts</h3>
            <p className="mt-1 text-sm text-slate-400">Subscribe and be the first to know when a new SPORTIVE event opens for registration.</p>
          </div>
          {subscribed ? (
            <p data-testid="subscribe-success" className="font-mono text-sm tracking-[0.15em] text-brand-olive">YOU'RE IN! WATCH YOUR INBOX.</p>
          ) : (
            <form onSubmit={subscribe} className="flex w-full max-w-md gap-2">
              <input data-testid="subscribe-email" type="email" required placeholder="Your email" className="input-k" value={email} onChange={(e) => setEmail(e.target.value)} />
              <button data-testid="subscribe-btn" className="btn-kinetic btn-primary-k shrink-0 !px-5 !py-2.5 text-xs">SUBSCRIBE</button>
            </form>
          )}
        </div>
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <img src="/sportive-logo.png" alt="SPORTIVE" className="h-11 w-auto object-contain drop-shadow-[0_0_16px_rgba(155,174,27,0.4)]" />
            <p className="mt-4 font-mono text-xs tracking-[0.25em] text-brand-olive">RUN • EXPLORE • CONNECT</p>
            <p className="mt-3 text-sm text-slate-400">Sports Tourism | Marathons | Trail Runs | Destination Experiences</p>
            <p className="mt-4 text-sm text-slate-500">Turning running events into destination experiences.</p>
          </div>
          <div>
            <h4 className="eyebrow !text-slate-500">QUICK LINKS</h4>
            <ul className="mt-4 space-y-2 text-sm">
              {[["Home", "/"], ["About", "/about"], ["Events", "/events"], ["RUNSAFARI", "/runsafari"], ["Destinations", "/destinations"], ["Sports Tourism", "/sports-tourism"], ["Partners", "/partners"], ["Gallery", "/gallery"], ["Stories", "/stories"], ["Contact", "/contact"]].map(([label, to]) => (
                <li key={to}><Link to={to} className="text-slate-400 transition-colors hover:text-brand-cyan">{label}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="eyebrow !text-slate-500">PARTICIPANT</h4>
            <ul className="mt-4 space-y-2 text-sm">
              <li><Link to="/events" className="text-slate-400 transition-colors hover:text-brand-cyan">Upcoming Events</Link></li>
              <li><Link to="/events" className="text-slate-400 transition-colors hover:text-brand-cyan">Registration</Link></li>
              <li><Link to="/results" className="text-slate-400 transition-colors hover:text-brand-cyan">My Registration</Link></li>
              <li><Link to="/results" className="text-slate-400 transition-colors hover:text-brand-cyan">Results</Link></li>
              <li><Link to="/results" className="text-slate-400 transition-colors hover:text-brand-cyan">Certificates</Link></li>
              <li><Link to="/gallery" className="text-slate-400 transition-colors hover:text-brand-cyan">Event Photos</Link></li>
              <li><Link to="/faq" className="text-slate-400 transition-colors hover:text-brand-cyan">FAQs</Link></li>
            </ul>
            <h4 className="eyebrow mt-8 !text-slate-500">FOR PARTNERS</h4>
            <ul className="mt-4 space-y-2 text-sm">
              <li><Link to="/partners" className="text-slate-400 transition-colors hover:text-brand-olive">Partner With Us</Link></li>
              <li><Link to="/partners" className="text-slate-400 transition-colors hover:text-brand-olive">Sponsorship</Link></li>
              <li><Link to="/partners" className="text-slate-400 transition-colors hover:text-brand-olive">Destination Projects</Link></li>
              <li><Link to="/partners#volunteer" className="text-slate-400 transition-colors hover:text-brand-olive">Volunteer With Us</Link></li>
              <li><Link to="/donation" className="text-slate-400 transition-colors hover:text-brand-olive">Donation — Run For A Cause</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="eyebrow !text-slate-500">CONNECT</h4>
            <ul className="mt-4 space-y-3 text-sm text-slate-400">
              <li className="flex items-center gap-2"><Mail size={15} className="text-brand-cyan" /> {s?.contact_email || "contact@sportive.co.in"}</li>
              <li className="flex items-center gap-2"><Phone size={15} className="text-brand-cyan" /> {s?.phone || "+91-79809278329"}</li>
              {(s?.phones || ["+91 84205 42712", "+91 70032 86491", "+91 87770 85121", "+91 98744 24239"]).map((p) => (
                <li key={p} className="flex items-center gap-2 pl-6 text-xs"><a href={`tel:${p.replace(/[^+\d]/g, "")}`} className="transition-colors hover:text-brand-cyan">{p}</a></li>
              ))}
            </ul>
            <div className="mt-5 flex gap-3">
              <a data-testid="footer-facebook" href={s?.facebook || "https://facebook.com/runsafari"} target="_blank" rel="noreferrer" className="rounded-full border border-brand-line p-2.5 text-slate-400 transition-colors hover:border-brand-cyan hover:text-brand-cyan"><Facebook size={17} /></a>
              <a data-testid="footer-instagram" href={s?.instagram || "https://instagram.com/udsportive"} target="_blank" rel="noreferrer" className="rounded-full border border-brand-line p-2.5 text-slate-400 transition-colors hover:border-brand-olive hover:text-brand-olive"><Instagram size={17} /></a>
              <a data-testid="footer-twitter" href={s?.twitter || "https://x.com/udsportive"} target="_blank" rel="noreferrer" title="X (Twitter)" className="rounded-full border border-brand-line p-2.5 text-slate-400 transition-colors hover:border-brand-cyan hover:text-brand-cyan"><Twitter size={17} /></a>
              <a data-testid="footer-youtube" href={s?.youtube || "https://www.youtube.com/@SPORTIVERUNSAFARI"} target="_blank" rel="noreferrer" title="YouTube" className="rounded-full border border-brand-line p-2.5 text-slate-400 transition-colors hover:border-brand-olive hover:text-brand-olive"><Youtube size={17} /></a>
            </div>
            <p className="mt-6 text-xs text-slate-600">{s?.address || "Kolkata, West Bengal, India"}</p>
          </div>
        </div>
        <div className="mt-14 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 border-t border-brand-line pt-6 font-mono text-[11px] tracking-[0.2em] text-slate-600">
          <span>© SPORTIVE. ALL RIGHTS RESERVED.</span>
          <Link to="/privacy" data-testid="footer-privacy" className="text-slate-500 transition-colors hover:text-brand-cyan">PRIVACY POLICY</Link>
          <Link to="/faq" data-testid="footer-faq" className="text-slate-500 transition-colors hover:text-brand-cyan">FAQ</Link>
          <span data-testid="visitor-count" className="text-brand-cyan">VISITORS: {visitors ?? "—"}</span>
        </div>
      </div>
    </footer>
  );
}
